import { Minus, Plus } from 'lucide-react';
import type { UnitsPreference } from '../utils/unitsPreference';

export interface ScaleControlsProps {
  /** The recipe's stored `servings`, if any — drives the "Makes N" stepper. */
  baseServings: number | undefined;
  factor: number;
  onFactorChange: (factor: number) => void;
  unitsPreference: UnitsPreference;
  onUnitsPreferenceChange: (preference: UnitsPreference) => void;
}

const MIN_FACTOR = 0.125;
const MAX_FACTOR = 8;

function clampFactor(factor: number): number {
  return Math.min(MAX_FACTOR, Math.max(MIN_FACTOR, factor));
}

/**
 * Screen E (Recipes detail): servings stepper + ×½/×2 quick actions, and a units toggle
 * (as written / US / metric). Both are pure *view* controls — the caller (`RecipeDetailPage`)
 * owns the actual scale factor / units state and feeds it to `scaleIngredientForDisplay`;
 * nothing here ever touches the stored recipe.
 */
export function ScaleControls({
  baseServings,
  factor,
  onFactorChange,
  unitsPreference,
  onUnitsPreferenceChange,
}: ScaleControlsProps): JSX.Element {
  const scaledServings = baseServings ? Math.max(1, Math.round(baseServings * factor)) : null;

  function stepServings(delta: number): void {
    if (!baseServings) return;
    const nextServings = Math.max(1, (scaledServings ?? baseServings) + delta);
    onFactorChange(clampFactor(nextServings / baseServings));
  }

  return (
    <div className="flex flex-col gap-2.5 border-b border-line px-4 py-3">
      <div className="flex items-center justify-between gap-3">
        {baseServings ? (
          <div className="flex items-center gap-3" data-testid="servings-stepper">
            <span className="text-[13px] font-semibold text-fg-3">Makes</span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                aria-label="Fewer servings"
                data-testid="servings-decrease"
                onClick={() => stepServings(-1)}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md border-2 border-line-strong text-fg"
              >
                <Minus className="h-4 w-4" aria-hidden="true" />
              </button>
              <span
                data-testid="servings-count"
                className="w-9 text-center text-[16px] font-semibold text-fg"
              >
                {scaledServings}
              </span>
              <button
                type="button"
                aria-label="More servings"
                data-testid="servings-increase"
                onClick={() => stepServings(1)}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md border-2 border-line-strong text-fg"
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </div>
        ) : (
          <span className="text-[13px] font-semibold text-fg-3" data-testid="scale-label">
            Scale ×{formatFactor(factor)}
          </span>
        )}
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            data-testid="scale-half"
            onClick={() => onFactorChange(clampFactor(factor * 0.5))}
            className="flex h-11 min-w-11 items-center justify-center rounded-md border-2 border-line-strong px-2 text-[13px] font-semibold text-fg"
          >
            ×½
          </button>
          <button
            type="button"
            data-testid="scale-double"
            onClick={() => onFactorChange(clampFactor(factor * 2))}
            className="flex h-11 min-w-11 items-center justify-center rounded-md border-2 border-line-strong px-2 text-[13px] font-semibold text-fg"
          >
            ×2
          </button>
          {factor !== 1 && (
            <button
              type="button"
              data-testid="scale-reset"
              onClick={() => onFactorChange(1)}
              className="flex h-11 min-w-11 items-center justify-center rounded-md px-2 text-[13px] font-semibold text-accent"
            >
              Reset
            </button>
          )}
        </div>
      </div>

      <div
        className="flex h-12 items-center gap-0.5 self-start rounded-md bg-raised p-0.5"
        data-testid="units-toggle"
        role="group"
        aria-label="Units"
      >
        {(
          [
            ['original', 'As written'],
            ['us', 'US'],
            ['metric', 'Metric'],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            data-testid={`units-${value}`}
            aria-pressed={unitsPreference === value}
            onClick={() => onUnitsPreferenceChange(value)}
            className={`flex h-11 items-center rounded px-3 text-[13px] font-semibold ${
              unitsPreference === value ? 'bg-surface text-fg shadow-sm' : 'text-fg-3'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

function formatFactor(factor: number): string {
  const rounded = Math.round(factor * 100) / 100;
  return Number.isInteger(rounded)
    ? String(rounded)
    : rounded.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
}
